/**
 * visa-doc-groups.ts — Wave 66 (Service 8)
 *
 * Deep-dive content for the six families of documents most often translated and
 * legalised for visa applications (both Thai visas for foreigners and foreign
 * visas for Thai nationals).
 *
 * VERIFIABLE SOURCES ONLY:
 *   - กรมการกงสุล กระทรวงการต่างประเทศ — งานนิติกรณ์เอกสาร (consular.mfa.go.th)
 *   - สำนักงานตรวจคนเข้าเมือง (immigration.go.th) — การยื่นขอ/ต่ออายุการอยู่ต่อในราชอาณาจักร
 *   - กองบัญชาการตำรวจสันติบาล — การตรวจสอบประวัติอาชญากรรม (Police Clearance Certificate)
 *   - กรมการปกครอง กระทรวงมหาดไทย — เอกสารทะเบียนราษฎร/ทะเบียนครอบครัว
 *   - กรมการจัดหางาน กระทรวงแรงงาน — ใบอนุญาตทำงาน
 *   - HCCH — สถานะอนุสัญญา Apostille ของไทย (src/data/apostille-status.ts)
 *
 * Rules enforced: no prices, no turnaround guarantees, no invented statistics,
 * no outcome guarantees, never state that Apostille is usable in Thailand today.
 * Requirements differ per embassy/consulate and change over time — every group
 * tells the reader to confirm with the destination authority.
 */
import { APOSTILLE_STATUS } from "@/data/apostille-status";

export interface VisaDocGroup {
  slug: string;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  summaryEn: string;
  bodyTh: string[];
  bodyEn: string[];
  documentsTh: string[];
  documentsEn: string[];
  chainTh: string[];
  chainEn: string[];
  checklistTh: string[];
  checklistEn: string[];
  faqsTh: { q: string; a: string }[];
  faqsEn: { q: string; a: string }[];
}

const APOSTILLE_TH =
  `ปัจจุบันประเทศไทยยังใช้ระบบรับรองนิติกรณ์แบบเดิม อนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) ` +
  `จะมีผลบังคับใช้กับประเทศไทยในวันที่ ${APOSTILLE_STATUS.entryIntoForceTh} ` +
  `หลังยื่นภาคยานุวัติสารเมื่อ ${APOSTILLE_STATUS.accessionDepositTh} (ที่มา: HCCH) ` +
  `ก่อนถึงวันดังกล่าว เอกสารประกอบวีซ่ายังต้องผ่านกรมการกงสุลและสถานทูตปลายทางตามลำดับเดิม`;

const APOSTILLE_EN =
  `Thailand still uses the traditional legalisation chain. The 1961 Hague Apostille Convention ` +
  `enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn}, after the deposit of its ` +
  `instrument of accession on ${APOSTILLE_STATUS.accessionDepositEn} (source: HCCH). Until then, visa ` +
  `supporting documents must still pass through the Department of Consular Affairs and the destination embassy.`;

const CONFIRM_TH =
  "ข้อกำหนดของเอกสารประกอบวีซ่าแตกต่างกันตามประเภทวีซ่า สัญชาติผู้ยื่น และสถานทูต/สถานกงสุลที่รับคำร้อง และเปลี่ยนแปลงได้ตลอดเวลา ควรตรวจสอบรายการเอกสารล่าสุดจากเว็บไซต์ทางการของหน่วยงานที่รับคำร้องก่อนเริ่มดำเนินการทุกครั้ง";

const CONFIRM_EN =
  "Supporting-document requirements vary by visa category, nationality and the embassy or consulate receiving the file, and they change over time. Always check the current checklist published by the receiving authority before you start.";

export const VISA_DOC_GROUPS: VisaDocGroup[] = [
  {
    slug: "identity-civil-status",
    titleTh: "เอกสารแสดงตนและสถานะทางทะเบียน",
    titleEn: "Identity and civil-status documents",
    summaryTh:
      "หนังสือเดินทาง บัตรประชาชน ทะเบียนบ้าน สูติบัตร ทะเบียนสมรส/หย่า และหนังสือรับรองความเป็นโสด — ชุดเอกสารพื้นฐานที่แทบทุกคำร้องขอวีซ่าต้องใช้ และเป็นกลุ่มที่ถูกตีกลับจากปัญหาการสะกดชื่อมากที่สุด",
    summaryEn:
      "Passport, ID card, house registration, birth certificate, marriage or divorce record and single-status affidavit — the baseline set for almost every visa file, and the family most often rejected over name spelling.",
    bodyTh: [
      "เอกสารแสดงตนและสถานะทางทะเบียนของไทยออกโดยสำนักทะเบียนอำเภอหรือสำนักงานเขต ภายใต้กรมการปกครอง กระทรวงมหาดไทย ทั้งหมดเป็นเอกสารภาษาไทย เมื่อจะใช้ประกอบคำร้องขอวีซ่าในต่างประเทศจึงต้องแปลเป็นภาษาอังกฤษ (หรือภาษาราชการของประเทศปลายทาง) และผ่านการรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ ก่อนนำไปยื่นสถานทูตปลายทาง",
      "ปัญหาที่พบมากที่สุดคือชื่อ-นามสกุลภาษาอังกฤษไม่ตรงกันระหว่างเอกสาร เช่น การสะกดในสูติบัตรฉบับแปลไม่ตรงกับหนังสือเดินทาง หรือคำนำหน้าชื่อและนามสกุลหลังสมรสไม่ตรงกับทะเบียนบ้านฉบับปัจจุบัน กฎที่ปลอดภัยที่สุดคือยึดการสะกดตามหนังสือเดินทางเป็นหลัก และหากเอกสารต้นฉบับสะกดต่างออกไป ให้ระบุการสะกดทั้งสองแบบไว้ในคำแปลเพื่อให้เจ้าหน้าที่เชื่อมโยงตัวบุคคลได้",
      "ความสดใหม่ของเอกสารก็สำคัญ สถานทูตหลายแห่งกำหนดให้เอกสารทะเบียนราษฎรและหนังสือรับรองสถานภาพต้องเป็นฉบับคัดรับรองรายการที่ออกใหม่ภายในระยะเวลาที่กำหนด ไม่ใช่สำเนาที่เก็บไว้เดิม จึงควรคัดเอกสารใหม่ก่อนเริ่มขั้นตอนแปลและรับรอง เพื่อไม่ให้เอกสารหมดอายุระหว่างทาง",
      "สำหรับชาวต่างชาติที่ยื่นคำร้องในประเทศไทย เอกสารแสดงตนที่ออกจากต่างประเทศต้องเดินทางย้อนทาง กล่าวคือ ต้องผ่านการรับรองจากหน่วยงานของประเทศต้นทางและสถานทูตของประเทศนั้นในประเทศไทย หรือผ่านสถานเอกอัครราชทูตไทยในประเทศต้นทาง ก่อนนำมาแปลเป็นภาษาไทยและรับรองที่กรมการกงสุลเพื่อใช้กับสำนักงานตรวจคนเข้าเมืองหรือหน่วยงานราชการไทย",
      APOSTILLE_TH,
      CONFIRM_TH,
    ],
    bodyEn: [
      "Thai identity and civil-status records are issued by district registration offices under the Department of Provincial Administration. They are Thai-language documents, so a visa file abroad needs an English translation (or a translation into the destination's official language) plus legalisation by the Department of Consular Affairs before the destination embassy stage.",
      "The most common failure is an inconsistent English spelling of the applicant's name across documents — a birth certificate translation that differs from the passport, or a married surname that does not match the current house registration. The safest rule is to follow the passport spelling and, where the source document differs, show both spellings in the translation so the officer can link the records.",
      "Freshness matters too. Many embassies require recently issued certified extracts of registration records and status affidavits rather than older copies, so obtain new extracts before starting the translation and legalisation chain to avoid them expiring mid-process.",
      "Foreign nationals filing inside Thailand face the mirror image: documents issued abroad must be certified in the issuing country and by that country's embassy in Thailand, or by the Royal Thai Embassy in the issuing country, before being translated into Thai and legalised for use with the Immigration Bureau or other Thai authorities.",
      APOSTILLE_EN,
      CONFIRM_EN,
    ],
    documentsTh: [
      "หนังสือเดินทาง (หน้าข้อมูลส่วนบุคคลและหน้าตราประทับที่เกี่ยวข้อง)",
      "บัตรประจำตัวประชาชน",
      "ทะเบียนบ้าน (ท.ร.14) / แบบรับรองรายการทะเบียนราษฎร",
      "สูติบัตร",
      "ทะเบียนสมรส (คร.2) / ใบสำคัญการสมรส (คร.3)",
      "ทะเบียนหย่า (คร.6) / ใบสำคัญการหย่า (คร.7)",
      "หนังสือรับรองความเป็นโสด / หนังสือรับรองสถานภาพการสมรส",
      "หนังสือสำคัญการเปลี่ยนชื่อตัว-ชื่อสกุล (ช.3 / ช.5)",
    ],
    documentsEn: [
      "Passport (bio-data page and relevant stamp pages)",
      "Thai national ID card",
      "House registration (Tor Ror 14) / certified registration extract",
      "Birth certificate",
      "Marriage registration and marriage certificate",
      "Divorce registration and divorce certificate",
      "Single-status or marital-status affidavit",
      "Change-of-name or change-of-surname certificate",
    ],
    chainTh: [
      "คัดเอกสารฉบับใหม่จากสำนักทะเบียน/สำนักงานเขต",
      "แปลเป็นภาษาอังกฤษหรือภาษาราชการปลายทาง พร้อมคำรับรองความถูกต้องของคำแปล",
      "ยื่นรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ",
      "ยื่นรับรองที่สถานทูต/สถานกงสุลของประเทศปลายทาง (ถ้าประเทศนั้นกำหนด)",
      "ยื่นชุดเอกสารพร้อมคำร้องขอวีซ่าตามช่องทางที่หน่วยงานกำหนด",
    ],
    chainEn: [
      "Obtain freshly issued extracts from the district registration office",
      "Translate into English or the destination's official language with a certificate of accuracy",
      "Legalise at the Department of Consular Affairs, Ministry of Foreign Affairs",
      "Legalise at the destination country's embassy or consulate, where required",
      "Submit with the visa application through the channel the authority specifies",
    ],
    checklistTh: [
      "สะกดชื่อ-นามสกุลในคำแปลตรงกับหนังสือเดินทางทุกฉบับ",
      "เอกสารทะเบียนเป็นฉบับคัดรับรองที่ออกใหม่ ไม่ใช่สำเนาเก่า",
      "ตรวจเลขประจำตัวประชาชน วันเดือนปีเกิด และที่อยู่ให้ตรงกันทุกฉบับ",
      "เตรียมสำเนาต้นฉบับแนบคู่คำแปลตามที่หน่วยงานปลายทางกำหนด",
      "ตรวจว่าประเทศปลายทางต้องการภาษาใด บางประเทศไม่รับคำแปลภาษาอังกฤษ",
    ],
    checklistEn: [
      "Name spelling in every translation matches the passport exactly",
      "Registration records are newly issued certified extracts, not old copies",
      "ID number, date of birth and address are consistent across all documents",
      "Copies of the originals are attached to the translations as required",
      "Confirm the required language — some countries do not accept English translations",
    ],
    faqsTh: [
      {
        q: "ชื่อในสูติบัตรสะกดไม่ตรงกับหนังสือเดินทาง ต้องแก้อย่างไร",
        a: "ในคำแปลควรสะกดตามหนังสือเดินทางเป็นหลัก และระบุการสะกดตามต้นฉบับกำกับไว้ด้วย เพื่อให้เจ้าหน้าที่เห็นว่าเป็นบุคคลเดียวกัน หากความต่างเกิดจากการเปลี่ยนชื่อ ให้แนบหนังสือสำคัญการเปลี่ยนชื่อที่แปลและรับรองแล้วประกอบ",
      },
      {
        q: "หนังสือรับรองความเป็นโสดมีอายุการใช้งานเท่าไร",
        a: "กฎหมายไทยไม่ได้กำหนดอายุของเอกสารไว้ แต่หน่วยงานปลายทางส่วนใหญ่กำหนดเองว่าต้องออกใหม่ภายในระยะเวลาหนึ่ง จึงต้องตรวจเงื่อนไขของสถานทูตหรือหน่วยงานที่รับคำร้องก่อน และควรคัดเอกสารใหม่ให้ใกล้วันยื่นที่สุด",
      },
      {
        q: "ต้องรับรองที่กรมการกงสุลก่อนหรือสถานทูตก่อน",
        a: "ลำดับคือกรมการกงสุลก่อนเสมอ แล้วจึงนำไปยื่นสถานทูตประเทศปลายทาง เพราะสถานทูตรับรองต่อจากลายมือชื่อเจ้าหน้าที่กรมการกงสุล ไม่ใช่รับรองเนื้อหาของเอกสารโดยตรง",
      },
      {
        q: "ใช้คำแปลที่แปลเองได้หรือไม่",
        a: "หน่วยงานส่วนใหญ่ต้องการคำแปลที่มีคำรับรองความถูกต้องของผู้แปลแนบท้าย และกรมการกงสุลจะตรวจเทียบคำแปลกับต้นฉบับก่อนรับรอง คำแปลที่ทำเองโดยไม่มีคำรับรองมักไม่ผ่านขั้นตอนนี้",
      },
      {
        q: "ประเทศไทยใช้ Apostille ได้แล้วหรือยัง",
        a: `ยังไม่ได้ อนุสัญญา Apostille จะมีผลกับประเทศไทยในวันที่ ${APOSTILLE_STATUS.entryIntoForceTh} (ที่มา: HCCH) ก่อนถึงวันนั้นยังต้องใช้ระบบรับรองกรมการกงสุลและสถานทูตตามเดิม`,
      },
    ],
    faqsEn: [
      {
        q: "My birth certificate name differs from my passport. What should the translation say?",
        a: "Use the passport spelling as the primary form and note the source-document spelling alongside it so the officer can see they refer to the same person. If the difference comes from a legal name change, attach the translated and legalised change-of-name certificate.",
      },
      {
        q: "How long is a single-status affidavit valid?",
        a: "Thai law sets no expiry, but receiving authorities usually impose their own recency rule. Check the embassy's published requirement and obtain the document as close to submission as practical.",
      },
      {
        q: "Which comes first, the Ministry of Foreign Affairs or the embassy?",
        a: "The Department of Consular Affairs always comes first. The embassy then certifies the consular officer's signature — it does not certify the content of the document itself.",
      },
      {
        q: "Can I translate the documents myself?",
        a: "Most authorities require a translation accompanied by a certificate of accuracy, and consular officers compare the translation against the original before legalising it. Self-made translations without certification usually fail at that stage.",
      },
      {
        q: "Can Thailand issue an Apostille yet?",
        a: `Not yet. The Convention enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn} (source: HCCH). Until then the consular plus embassy chain still applies.`,
      },
    ],
  },
  {
    slug: "financial-evidence",
    titleTh: "หลักฐานทางการเงินและการรับรองผู้สนับสนุน",
    titleEn: "Financial evidence and sponsorship",
    summaryTh:
      "Statement บัญชีธนาคาร หนังสือรับรองยอดเงินฝาก สลิปเงินเดือน หลักฐานภาษี และหนังสือรับรองการสนับสนุนค่าใช้จ่าย — กลุ่มเอกสารที่สถานทูตใช้ประเมินความสามารถในการรับผิดชอบค่าใช้จ่ายระหว่างพำนัก",
    summaryEn:
      "Bank statements, bank certification letters, payslips, tax records and sponsorship letters — the evidence embassies use to assess whether the applicant can support the intended stay.",
    bodyTh: [
      "หลักฐานทางการเงินเป็นเอกสารที่สถานทูตให้น้ำหนักสูงในการพิจารณา เพราะใช้ประเมินว่าผู้ยื่นมีเงินเพียงพอต่อการเดินทางและการพำนักตามระยะเวลาที่ขอ เอกสารกลุ่มนี้ออกโดยธนาคารพาณิชย์ นายจ้าง หรือหน่วยงานสรรพากร และมักต้องขอเป็นฉบับภาษาอังกฤษโดยตรงจากผู้ออกเอกสารได้ ซึ่งช่วยลดขั้นตอนการแปล",
      "หากธนาคารออกให้เฉพาะภาษาไทย ต้องแปลพร้อมคำรับรองความถูกต้อง และหากประเทศปลายทางกำหนดให้เอกสารต้องผ่านนิติกรณ์ ก็ต้องนำไปรับรองที่กรมการกงสุลและสถานทูตตามลำดับ ทั้งนี้เอกสารที่ออกโดยเอกชน เช่น หนังสือรับรองจากธนาคารหรือหนังสือรับรองเงินเดือน โดยทั่วไปต้องผ่านการรับรองลายมือชื่อผู้มีอำนาจลงนามก่อน จึงจะเข้าสู่ขั้นตอนนิติกรณ์ได้",
      "ประเด็นที่ทำให้คำร้องถูกปฏิเสธบ่อยคือเงินก้อนใหญ่ที่เพิ่งเข้าบัญชีก่อนยื่นไม่นาน โดยไม่มีคำอธิบายที่มาของเงิน สถานทูตหลายแห่งดู statement ย้อนหลังหลายเดือนเพื่อดูความสม่ำเสมอของกระแสเงิน ไม่ได้ดูเพียงยอดคงเหลือวันสุดท้าย การเตรียมเอกสารอธิบายที่มาของเงิน เช่น สัญญาขายทรัพย์สินหรือหนังสือรับรองโบนัส จึงช่วยลดความเสี่ยง",
      "กรณีมีผู้สนับสนุนค่าใช้จ่าย เอกสารชุดนี้จะรวมหนังสือรับรองการสนับสนุน เอกสารแสดงตนของผู้สนับสนุน และหลักฐานความสัมพันธ์ เช่น สูติบัตรหรือทะเบียนสมรส หนังสือรับรองการสนับสนุนที่ทำในประเทศไทยมักต้องผ่านการรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ก่อนเข้าสู่ขั้นตอนกรมการกงสุล",
      CONFIRM_TH,
    ],
    bodyEn: [
      "Financial evidence carries substantial weight because it shows whether the applicant can fund the trip and the intended stay. These documents come from commercial banks, employers or the Revenue Department, and many issuers can produce an English version directly — which removes a translation step.",
      "Where the bank issues Thai-only documents, they need a translation with a certificate of accuracy, and if the destination requires legalisation, the file then goes through the Department of Consular Affairs and the embassy. Privately issued documents such as bank letters or salary certificates normally need the signatory's signature certified before they can enter the consular stage.",
      "A frequent refusal trigger is a large deposit landing shortly before submission with no explanation of its source. Many embassies review several months of statements to judge the consistency of the balance rather than the closing figure alone, so supporting evidence for unusual credits — a property sale contract or a bonus letter — reduces risk.",
      "Where a sponsor funds the trip, the set also includes a sponsorship letter, the sponsor's identity documents and proof of relationship such as a birth or marriage certificate. Sponsorship letters executed in Thailand usually need signature certification by a Notarial Services Attorney before the consular stage.",
      CONFIRM_EN,
    ],
    documentsTh: [
      "Statement บัญชีธนาคารย้อนหลัง (ตามจำนวนเดือนที่หน่วยงานปลายทางกำหนด)",
      "หนังสือรับรองยอดเงินฝากจากธนาคาร (Bank Certification Letter)",
      "สลิปเงินเดือนย้อนหลังและหนังสือรับรองเงินเดือน",
      "หนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) และแบบแสดงรายการภาษี",
      "หนังสือรับรองการสนับสนุนค่าใช้จ่าย (Sponsorship / Affidavit of Support)",
      "หลักฐานทรัพย์สิน เช่น โฉนดที่ดิน หรือทะเบียนรถ (ถ้าต้องการแสดงเพิ่ม)",
    ],
    documentsEn: [
      "Bank statements covering the period the authority requires",
      "Bank certification letter confirming the balance",
      "Recent payslips and an employer salary certificate",
      "Withholding-tax certificate and personal income-tax filings",
      "Sponsorship letter or affidavit of support",
      "Asset evidence such as title deeds or vehicle registration, if offered as extra support",
    ],
    chainTh: [
      "ขอเอกสารจากธนาคาร/นายจ้าง โดยขอฉบับภาษาอังกฤษถ้าออกได้",
      "แปลและรับรองคำแปลเฉพาะฉบับที่ออกเป็นภาษาไทย",
      "รับรองลายมือชื่อผู้มีอำนาจลงนาม (สำหรับเอกสารเอกชน) โดยทนายความผู้ทำคำรับรอง",
      "ยื่นนิติกรณ์ที่กรมการกงสุล เมื่อประเทศปลายทางกำหนด",
      "ยื่นรับรองสถานทูตปลายทางและยื่นพร้อมคำร้องขอวีซ่า",
    ],
    chainEn: [
      "Request documents from the bank or employer, in English where available",
      "Translate and certify only the Thai-language items",
      "Certify the signatory's signature for privately issued documents via a Notarial Services Attorney",
      "Legalise at the Department of Consular Affairs where the destination requires it",
      "Legalise at the destination embassy and file with the visa application",
    ],
    checklistTh: [
      "ระยะเวลา statement ครอบคลุมตามที่หน่วยงานปลายทางกำหนด",
      "ชื่อเจ้าของบัญชีตรงกับหนังสือเดินทาง",
      "มีคำอธิบายที่มาของเงินก้อนใหญ่ที่เพิ่งเข้าบัญชี",
      "เอกสารธนาคารมีตราประทับและลายมือชื่อเจ้าหน้าที่ครบถ้วน",
      "หนังสือรับรองผู้สนับสนุนแนบหลักฐานความสัมพันธ์และเอกสารแสดงตนของผู้สนับสนุน",
    ],
    checklistEn: [
      "Statement period matches the receiving authority's requirement",
      "Account holder's name matches the passport",
      "Unusual large credits are explained with supporting evidence",
      "Bank documents carry the branch stamp and officer's signature",
      "Sponsorship letters include proof of relationship and the sponsor's identity documents",
    ],
    faqsTh: [
      {
        q: "ต้องใช้ statement ย้อนหลังกี่เดือน",
        a: "ไม่มีมาตรฐานเดียวกันทุกประเทศ แต่ละสถานทูตกำหนดเอง บางแห่งขอ 3 เดือน บางแห่งขอ 6 เดือน จึงต้องดูรายการเอกสารล่าสุดของสถานทูตที่รับคำร้องเป็นหลัก",
      },
      {
        q: "หนังสือรับรองยอดเงินฝากจากธนาคารต้องแปลไหม",
        a: "ธนาคารส่วนใหญ่ออกฉบับภาษาอังกฤษได้ตามคำขอ หากได้ฉบับภาษาอังกฤษจากธนาคารโดยตรงก็ไม่ต้องแปล แต่หากออกเป็นภาษาไทยต้องแปลพร้อมคำรับรองความถูกต้อง",
      },
      {
        q: "ผู้สนับสนุนอยู่ต่างประเทศ ต้องทำเอกสารอย่างไร",
        a: "โดยทั่วไปผู้สนับสนุนจะจัดทำหนังสือรับรองการสนับสนุนและรับรองลายมือชื่อในประเทศที่ตนอยู่ ตามระบบรับรองของประเทศนั้น แล้วส่งฉบับที่รับรองแล้วมาให้ผู้ยื่นแนบกับคำร้อง ควรตรวจเงื่อนไขของสถานทูตที่รับคำร้องประกอบด้วย",
      },
      {
        q: "หลักฐานการเงินต้องผ่านกรมการกงสุลทุกครั้งหรือไม่",
        a: "ไม่เสมอไป หลายสถานทูตรับเอกสารการเงินฉบับภาษาอังกฤษจากธนาคารโดยตรงโดยไม่ต้องนิติกรณ์ แต่บางประเทศโดยเฉพาะกลุ่มที่ต้องรับรองผ่านสถานทูตกำหนดให้ผ่านกรมการกงสุลก่อน จึงต้องตรวจรายการเอกสารของแต่ละแห่ง",
      },
    ],
    faqsEn: [
      {
        q: "How many months of bank statements are needed?",
        a: "There is no single standard; each embassy sets its own period — commonly three or six months. Follow the checklist published by the embassy handling your application.",
      },
      {
        q: "Does a bank certification letter need translating?",
        a: "Most Thai banks will issue the letter in English on request, in which case no translation is needed. If it is issued in Thai only, it requires a translation with a certificate of accuracy.",
      },
      {
        q: "My sponsor lives abroad — how should their letter be prepared?",
        a: "The sponsor normally signs the letter and has the signature certified in their own country under that country's certification system, then sends the certified original to be filed with the application. Check the receiving embassy's specific requirement as well.",
      },
      {
        q: "Must financial evidence always be legalised?",
        a: "Not always. Many embassies accept English-language bank documents directly. Countries that use the consular-plus-embassy chain may require legalisation first, so check the destination's checklist.",
      },
    ],
  },
  {
    slug: "education-records",
    titleTh: "เอกสารการศึกษาสำหรับวีซ่านักเรียนและวีซ่าทำงาน",
    titleEn: "Education records for study and work visas",
    summaryTh:
      "ปริญญาบัตร ทรานสคริปต์ หนังสือรับรองสถานภาพนักศึกษา และหนังสือตอบรับจากสถาบัน — ใช้ทั้งกับวีซ่านักเรียนและวีซ่าทำงานที่ต้องพิสูจน์คุณวุฒิ",
    summaryEn:
      "Degree certificates, transcripts, enrolment letters and offers of admission — used both for student visas and for work visas that require proof of qualifications.",
    bodyTh: [
      "เอกสารการศึกษาที่ออกโดยสถาบันในประเทศไทยเป็นเอกสารของหน่วยงานผู้ออก ไม่ใช่เอกสารทะเบียนราษฎร ขั้นตอนจึงเริ่มจากการขอฉบับจริงหรือฉบับที่สถาบันรับรองสำเนาถูกต้อง หลายมหาวิทยาลัยออกปริญญาบัตรและทรานสคริปต์ฉบับภาษาอังกฤษได้เอง ซึ่งเป็นทางที่เรียบง่ายที่สุดและลดโอกาสคำแปลไม่ตรงศัพท์เฉพาะของหลักสูตร",
      "เมื่อสถาบันออกได้เฉพาะภาษาไทย ต้องแปลพร้อมคำรับรองความถูกต้อง โดยต้องระวังคำเฉพาะ เช่น ชื่อปริญญา ชื่อสาขาวิชา ระบบเกรด และหน่วยกิต ให้ตรงตามที่สถาบันใช้จริง เพราะหน่วยงานเทียบวุฒิในต่างประเทศจะเทียบคำเหล่านี้กับกรอบคุณวุฒิของประเทศตน หากแปลคลาดเคลื่อนอาจทำให้ผลการเทียบวุฒิผิดพลาด",
      "สำหรับวีซ่านักเรียน สถานทูตมักขอหนังสือตอบรับจากสถาบันปลายทางควบคู่กับหลักฐานการเงินและหลักฐานที่พัก ส่วนวีซ่าทำงานที่ต้องพิสูจน์คุณวุฒิ เช่น การขอใบอนุญาตทำงานในไทยตามระเบียบกรมการจัดหางาน จะใช้ปริญญาบัตรและทรานสคริปต์ที่ผ่านการรับรองแล้วเป็นหลักฐานประกอบ",
      "เอกสารการศึกษาที่ออกจากต่างประเทศและจะใช้ในไทย ต้องผ่านการรับรองจากหน่วยงานของประเทศต้นทางและสถานทูตประเทศนั้นในไทย หรือสถานเอกอัครราชทูตไทยในประเทศต้นทาง ก่อนแปลเป็นภาษาไทยและรับรองนิติกรณ์ที่กรมการกงสุล",
      APOSTILLE_TH,
      CONFIRM_TH,
    ],
    bodyEn: [
      "Education records are institutional documents rather than civil-registration records, so the chain begins with the original or an institution-certified copy. Many Thai universities issue English degree certificates and transcripts themselves, which is the cleanest route and avoids mistranslated programme terminology.",
      "When the institution issues Thai only, the documents need a translation with a certificate of accuracy, and the terminology must mirror the institution's own usage — degree name, field of study, grading scale and credit system. Foreign credential-evaluation bodies map those terms onto their national qualification frameworks, so an inaccurate rendering can distort the evaluation.",
      "For student visas, embassies typically require the destination institution's offer or enrolment letter alongside financial and accommodation evidence. For work visas requiring proof of qualifications — including Thai work-permit applications under Department of Employment rules — legalised degree certificates and transcripts serve as the supporting evidence.",
      "Foreign-issued education records intended for use in Thailand must first be certified in the issuing country and by that country's embassy in Thailand, or by the Royal Thai Embassy abroad, before being translated into Thai and legalised at the Department of Consular Affairs.",
      APOSTILLE_EN,
      CONFIRM_EN,
    ],
    documentsTh: [
      "ปริญญาบัตร / ประกาศนียบัตร",
      "ใบแสดงผลการศึกษา (Transcript)",
      "หนังสือรับรองสถานภาพนักศึกษา / หนังสือรับรองการสำเร็จการศึกษา",
      "หนังสือตอบรับเข้าศึกษาจากสถาบันปลายทาง",
      "ใบรับรองผลสอบภาษา (ตามที่สถาบันหรือสถานทูตกำหนด)",
      "หนังสือรับรองหลักสูตรและจำนวนชั่วโมงเรียน (กรณีหลักสูตรระยะสั้น)",
    ],
    documentsEn: [
      "Degree certificate or diploma",
      "Academic transcript",
      "Enrolment or graduation certificate",
      "Offer or acceptance letter from the destination institution",
      "Language test results, where the institution or embassy requires them",
      "Course and contact-hour certificate for short programmes",
    ],
    chainTh: [
      "ขอเอกสารฉบับจริงหรือฉบับรับรองจากสถาบัน โดยขอฉบับภาษาอังกฤษถ้าออกได้",
      "แปลและรับรองคำแปลเฉพาะฉบับภาษาไทย โดยยึดศัพท์ตามที่สถาบันใช้",
      "รับรองลายมือชื่อผู้มีอำนาจลงนามของสถาบันเมื่อหน่วยงานปลายทางกำหนด",
      "ยื่นนิติกรณ์ที่กรมการกงสุล",
      "ยื่นรับรองสถานทูตปลายทางแล้วยื่นพร้อมคำร้อง",
    ],
    chainEn: [
      "Obtain originals or institution-certified copies, in English where available",
      "Translate the Thai-language items using the institution's own terminology",
      "Certify the institutional signatory where the destination requires it",
      "Legalise at the Department of Consular Affairs",
      "Legalise at the destination embassy and submit with the application",
    ],
    checklistTh: [
      "ชื่อปริญญาและสาขาในคำแปลตรงกับที่สถาบันใช้",
      "ทรานสคริปต์แสดงระบบเกรดและหน่วยกิตครบถ้วน",
      "ชื่อผู้สำเร็จการศึกษาสะกดตรงกับหนังสือเดินทาง",
      "แนบสำเนาต้นฉบับคู่กับคำแปลทุกฉบับ",
      "ตรวจว่าปลายทางต้องการเอกสารรับรองจากสถาบันโดยตรงหรือรับฉบับรับรองสำเนาได้",
    ],
    checklistEn: [
      "Degree and field names in the translation match the institution's wording",
      "The transcript shows the full grading scale and credit system",
      "The graduate's name matches the passport spelling",
      "Copies of the originals accompany every translation",
      "Confirm whether the destination requires documents sent directly by the institution",
    ],
    faqsTh: [
      {
        q: "มหาวิทยาลัยออกทรานสคริปต์ภาษาอังกฤษให้แล้ว ยังต้องแปลอีกไหม",
        a: "ไม่ต้องแปล เพราะเป็นเอกสารภาษาอังกฤษที่ออกโดยผู้ออกเอกสารเอง แต่หากปลายทางกำหนดให้ผ่านนิติกรณ์ ยังต้องนำไปรับรองที่กรมการกงสุลและสถานทูตตามลำดับ",
      },
      {
        q: "ปริญญาบัตรฉบับจริงต้องส่งไปรับรองด้วยหรือไม่",
        a: "ขั้นตอนนิติกรณ์จะตรวจเทียบกับต้นฉบับ จึงต้องนำต้นฉบับไปแสดง ส่วนจะรับรองบนสำเนาหรือคำแปลขึ้นอยู่กับรูปแบบที่ผู้ยื่นต้องการใช้และเงื่อนไขของหน่วยงานปลายทาง",
      },
      {
        q: "จบจากต่างประเทศแล้วจะใช้ในไทย ต้องทำอะไรบ้าง",
        a: "ต้องผ่านการรับรองตามระบบของประเทศที่ออกเอกสารก่อน แล้วรับรองที่สถานทูตของประเทศนั้นในไทยหรือสถานเอกอัครราชทูตไทยในประเทศนั้น จากนั้นจึงแปลเป็นภาษาไทยและยื่นนิติกรณ์ที่กรมการกงสุล",
      },
      {
        q: "วีซ่านักเรียนต้องใช้เอกสารการเงินด้วยไหม",
        a: "ส่วนใหญ่ต้องใช้ควบคู่กัน เพราะสถานทูตพิจารณาทั้งความพร้อมด้านการศึกษาและความสามารถในการรับผิดชอบค่าใช้จ่าย รายละเอียดขึ้นอยู่กับประเทศปลายทาง",
      },
    ],
    faqsEn: [
      {
        q: "My university issued an English transcript — do I still need a translation?",
        a: "No, because the issuer produced it in English. If the destination requires legalisation, it still passes through the Department of Consular Affairs and then the embassy.",
      },
      {
        q: "Do I have to present the original degree certificate?",
        a: "Consular officers compare against the original, so the original must be shown. Whether the certification is placed on a copy or on the translation depends on the format you need and the destination's rule.",
      },
      {
        q: "I graduated abroad and want to use the degree in Thailand. What is the route?",
        a: "Certify it under the issuing country's system, then have it certified by that country's embassy in Thailand or by the Royal Thai Embassy abroad, then translate into Thai and legalise at the Department of Consular Affairs.",
      },
      {
        q: "Do student visas also need financial evidence?",
        a: "Usually yes — embassies assess both academic readiness and the ability to fund the stay. The exact requirement depends on the destination.",
      },
    ],
  },
  {
    slug: "employment-and-work-permit",
    titleTh: "เอกสารการจ้างงานและใบอนุญาตทำงาน",
    titleEn: "Employment and work-permit documents",
    summaryTh:
      "สัญญาจ้าง หนังสือรับรองการทำงาน หนังสือรับรองบริษัท และเอกสารประกอบใบอนุญาตทำงาน — ใช้กับวีซ่าประเภททำงานทั้งขาเข้าไทยและขาออกไปต่างประเทศ",
    summaryEn:
      "Employment contracts, employment certificates, company records and work-permit paperwork — used for work-category visas both into Thailand and abroad.",
    bodyTh: [
      "วีซ่าประเภททำงานตรวจสอบสองเรื่องคู่กันเสมอ คือสถานะของนายจ้างและคุณสมบัติของผู้ถูกจ้าง เอกสารฝั่งนายจ้างในไทยได้แก่ หนังสือรับรองบริษัทจากกรมพัฒนาธุรกิจการค้า บัญชีรายชื่อผู้ถือหุ้น งบการเงิน และแบบยื่นภาษี ส่วนฝั่งผู้ถูกจ้างได้แก่ สัญญาจ้าง หนังสือรับรองการทำงาน และหลักฐานคุณวุฒิ",
      "สำหรับชาวต่างชาติที่จะทำงานในประเทศไทย การขอใบอนุญาตทำงานอยู่ในความรับผิดชอบของกรมการจัดหางาน กระทรวงแรงงาน และการอยู่ต่อในราชอาณาจักรอยู่ในความรับผิดชอบของสำนักงานตรวจคนเข้าเมือง สองเรื่องนี้เป็นคนละกระบวนการที่ต้องสอดคล้องกัน เอกสารต่างประเทศที่ใช้ประกอบต้องผ่านการรับรองและแปลเป็นภาษาไทยตามที่หน่วยงานกำหนด",
      "สำหรับคนไทยที่จะไปทำงานต่างประเทศ เอกสารฝั่งไทย เช่น หนังสือรับรองการทำงาน หนังสือรับรองเงินเดือน และหนังสือรับรองบริษัท มักต้องผ่านการรับรองลายมือชื่อผู้มีอำนาจลงนามก่อน แล้วจึงยื่นนิติกรณ์ที่กรมการกงสุลและสถานทูตปลายทาง เพราะเป็นเอกสารที่ออกโดยเอกชน ไม่ใช่เอกสารราชการโดยตรง",
      "จุดที่ควรระวังคือความสอดคล้องของข้อมูลระหว่างเอกสาร เช่น ตำแหน่งงานในสัญญาจ้างต้องตรงกับตำแหน่งในหนังสือรับรองการทำงานและในคำร้อง หากข้อมูลไม่ตรงกันเจ้าหน้าที่อาจขอเอกสารเพิ่มเติมหรือส่งเรื่องกลับให้แก้ไข",
      CONFIRM_TH,
    ],
    bodyEn: [
      "Work-category visas examine two things together: the employer's standing and the employee's qualifications. On the Thai employer side that means the company affidavit from the Department of Business Development, the shareholder list, financial statements and tax filings; on the employee side, the employment contract, employment certificate and proof of qualifications.",
      "For foreigners working in Thailand, work permits fall under the Department of Employment, Ministry of Labour, while permission to stay falls under the Immigration Bureau. They are separate processes that must line up, and foreign supporting documents must be certified and translated into Thai as those agencies require.",
      "For Thai nationals taking up work abroad, Thai-side documents such as employment certificates, salary letters and company affidavits usually need the signatory's signature certified first, then legalisation at the Department of Consular Affairs and the destination embassy, because they are privately issued rather than public records.",
      "Consistency across the file matters: the job title in the contract should match the employment certificate and the application form. Mismatches typically lead to requests for further documents or a returned file.",
      CONFIRM_EN,
    ],
    documentsTh: [
      "สัญญาจ้างแรงงาน / หนังสือเสนอจ้าง",
      "หนังสือรับรองการทำงานและหนังสือรับรองเงินเดือน",
      "หนังสือรับรองนิติบุคคลจากกรมพัฒนาธุรกิจการค้า",
      "บัญชีรายชื่อผู้ถือหุ้น (บอจ.5) และงบการเงิน",
      "ใบอนุญาตทำงาน (สำหรับผู้ที่ทำงานในไทยอยู่แล้ว)",
      "หนังสือมอบอำนาจของบริษัท (กรณีผู้แทนยื่นแทน)",
    ],
    documentsEn: [
      "Employment contract or offer letter",
      "Employment certificate and salary letter",
      "Company affidavit from the Department of Business Development",
      "Shareholder list and financial statements",
      "Existing Thai work permit, where applicable",
      "Corporate power of attorney where a representative files",
    ],
    chainTh: [
      "รวบรวมเอกสารนายจ้างฉบับล่าสุดจากหน่วยงานผู้ออก",
      "แปลเอกสารภาษาไทยพร้อมคำรับรองความถูกต้อง",
      "รับรองลายมือชื่อกรรมการหรือผู้มีอำนาจลงนามสำหรับเอกสารเอกชน",
      "ยื่นนิติกรณ์ที่กรมการกงสุล",
      "ยื่นรับรองสถานทูตปลายทาง หรือยื่นต่อกรมการจัดหางาน/ตม. ตามกรณี",
    ],
    chainEn: [
      "Collect current employer records from the issuing agencies",
      "Translate the Thai-language items with a certificate of accuracy",
      "Certify the director's or authorised signatory's signature for private documents",
      "Legalise at the Department of Consular Affairs",
      "Legalise at the destination embassy, or file with the Department of Employment or Immigration Bureau as applicable",
    ],
    checklistTh: [
      "ตำแหน่งงานตรงกันทุกฉบับและตรงกับคำร้อง",
      "หนังสือรับรองนิติบุคคลออกใหม่ภายในระยะเวลาที่หน่วยงานกำหนด",
      "ลายมือชื่อผู้มีอำนาจลงนามตรงกับที่ปรากฏในหนังสือรับรองนิติบุคคล",
      "เอกสารมีตราประทับบริษัทครบตามที่หนังสือรับรองระบุ",
      "เอกสารต่างประเทศผ่านการรับรองจากประเทศต้นทางแล้วก่อนแปลเป็นภาษาไทย",
    ],
    checklistEn: [
      "The job title is identical across every document and the application",
      "The company affidavit is recent enough for the receiving agency",
      "The signatory matches the authorised director named in the affidavit",
      "The company seal is affixed where the affidavit requires it",
      "Foreign documents were certified in the issuing country before Thai translation",
    ],
    faqsTh: [
      {
        q: "ใบอนุญาตทำงานกับวีซ่าทำงานคือเรื่องเดียวกันหรือไม่",
        a: "เป็นคนละเรื่อง ใบอนุญาตทำงานออกโดยกรมการจัดหางาน กระทรวงแรงงาน ส่วนการตรวจลงตราและการอยู่ต่อในราชอาณาจักรอยู่ในอำนาจของกระทรวงการต่างประเทศและสำนักงานตรวจคนเข้าเมือง ต้องดำเนินการให้สอดคล้องกันทั้งสองส่วน",
      },
      {
        q: "หนังสือรับรองบริษัทต้องออกใหม่ภายในกี่วัน",
        a: "แต่ละหน่วยงานกำหนดต่างกัน ในทางปฏิบัติมักขอฉบับที่ออกใหม่ไม่นานก่อนวันยื่น จึงควรตรวจเงื่อนไขของหน่วยงานที่รับคำร้องและคัดฉบับใหม่ให้ใกล้วันยื่น",
      },
      {
        q: "สัญญาจ้างภาษาอังกฤษต้องแปลเป็นภาษาไทยไหม",
        a: "หากยื่นต่อหน่วยงานราชการไทย โดยทั่วไปต้องมีคำแปลภาษาไทยแนบ เพราะภาษาราชการที่ใช้ในการพิจารณาคือภาษาไทย ควรตรวจแบบฟอร์มและรายการเอกสารของหน่วยงานที่รับคำร้อง",
      },
      {
        q: "เอกสารบริษัทที่ออกโดยเอกชนรับรองนิติกรณ์ได้เลยไหม",
        a: "โดยทั่วไปต้องผ่านการรับรองลายมือชื่อผู้มีอำนาจลงนามก่อน จึงจะเข้าสู่ขั้นตอนนิติกรณ์ได้ เพราะกรมการกงสุลรับรองลายมือชื่อของผู้รับรองในลำดับก่อนหน้า",
      },
    ],
    faqsEn: [
      {
        q: "Is a work permit the same as a work visa?",
        a: "No. Work permits are issued by the Department of Employment, Ministry of Labour, while the visa and permission to stay sit with the Ministry of Foreign Affairs and the Immigration Bureau. Both must align.",
      },
      {
        q: "How recent must the company affidavit be?",
        a: "Each receiving agency sets its own rule; in practice a recently issued affidavit is expected, so obtain a fresh copy close to the filing date.",
      },
      {
        q: "Does an English employment contract need a Thai translation?",
        a: "For filings with Thai government agencies a Thai translation is generally required, since Thai is the language of the proceedings. Check the receiving agency's document list.",
      },
      {
        q: "Can privately issued company documents go straight to legalisation?",
        a: "Usually not — the authorised signatory's signature must be certified first, because the consular stage certifies the preceding certifier's signature.",
      },
    ],
  },
  {
    slug: "police-clearance-and-medical",
    titleTh: "หนังสือรับรองความประพฤติและใบรับรองแพทย์",
    titleEn: "Police clearance and medical certificates",
    summaryTh:
      "หนังสือรับรองความประพฤติ (Police Clearance Certificate) และใบรับรองแพทย์ — เอกสารที่หลายประเทศกำหนดสำหรับวีซ่าระยะยาว วีซ่าทำงาน และวีซ่าถิ่นที่อยู่",
    summaryEn:
      "Police clearance certificates and medical certificates — required by many countries for long-stay, work and residence visas.",
    bodyTh: [
      "หนังสือรับรองความประพฤติสำหรับผู้ที่อยู่ในประเทศไทยออกโดยกองบัญชาการตำรวจสันติบาล สำนักงานตำรวจแห่งชาติ ผู้ยื่นต้องพิมพ์ลายนิ้วมือและยื่นเอกสารแสดงตนตามที่หน่วยงานกำหนด สำหรับคนไทยที่พำนักอยู่ต่างประเทศ สามารถดำเนินการผ่านสถานเอกอัครราชทูตหรือสถานกงสุลใหญ่ไทยในประเทศที่พำนัก ตามขั้นตอนที่แต่ละแห่งประกาศ",
      "เอกสารฉบับนี้มักออกเป็นภาษาอังกฤษอยู่แล้ว ทำให้ไม่ต้องแปลในหลายกรณี แต่หากประเทศปลายทางใช้ภาษาอื่น เช่น จีน ญี่ปุ่น เกาหลี หรือภาษาในกลุ่มตะวันออกกลาง ต้องแปลเป็นภาษานั้นและผ่านการรับรองตามลำดับ กรมการกงสุลแล้วจึงสถานทูตปลายทาง",
      "อายุการใช้งานเป็นประเด็นสำคัญ ประเทศปลายทางส่วนใหญ่กำหนดให้หนังสือรับรองความประพฤติออกใหม่ภายในระยะเวลาที่กำหนดก่อนวันยื่น การขอเอกสารเร็วเกินไปแล้วเก็บไว้หลายเดือนอาจทำให้ต้องขอใหม่ทั้งชุด จึงควรวางลำดับเวลาให้เอกสารยังอยู่ในช่วงที่ใช้ได้เมื่อถึงวันยื่น",
      "ใบรับรองแพทย์สำหรับวีซ่ามีสองแบบที่ต่างกัน แบบแรกคือใบรับรองแพทย์ทั่วไปตามแบบที่หน่วยงานไทยกำหนด อีกแบบคือการตรวจสุขภาพกับสถานพยาบาลที่สถานทูตปลายทางกำหนดไว้เท่านั้น (panel clinic) ซึ่งผลตรวจมักส่งตรงถึงหน่วยงานปลายทางและไม่ต้องผ่านการแปลหรือนิติกรณ์ ควรตรวจให้ชัดว่าประเทศปลายทางใช้ระบบใดก่อนไปตรวจ",
      CONFIRM_TH,
    ],
    bodyEn: [
      "Police clearance certificates for applicants inside Thailand are issued by the Royal Thai Police Special Branch. Applicants provide fingerprints and identity documents as the unit specifies. Thai nationals living abroad can generally apply through the Royal Thai Embassy or Consulate-General where they reside, following that post's published procedure.",
      "The certificate is normally issued in English, so translation is often unnecessary. Where the destination uses another language — Chinese, Japanese, Korean or Middle Eastern languages, for example — the document needs translation and then legalisation, first at the Department of Consular Affairs and then at the destination embassy.",
      "Validity is critical. Most destinations require the certificate to have been issued within a set window before filing, so obtaining it too early can mean repeating the whole exercise. Sequence the steps so the certificate is still inside its window on the submission date.",
      "Medical evidence comes in two distinct forms. One is a general medical certificate on the form a Thai agency prescribes. The other is an examination at a clinic designated by the destination embassy (a panel clinic), where results usually go directly to the authority and need neither translation nor legalisation. Confirm which system applies before booking.",
      CONFIRM_EN,
    ],
    documentsTh: [
      "หนังสือรับรองความประพฤติจากกองบัญชาการตำรวจสันติบาล",
      "แบบพิมพ์ลายนิ้วมือตามที่หน่วยงานกำหนด",
      "หนังสือเดินทางและบัตรประจำตัวประชาชน",
      "ทะเบียนบ้านหรือหลักฐานที่อยู่",
      "ใบรับรองแพทย์ตามแบบที่หน่วยงานปลายทางกำหนด",
      "ผลตรวจสุขภาพจากสถานพยาบาลที่สถานทูตกำหนด (กรณีใช้ระบบ panel clinic)",
    ],
    documentsEn: [
      "Police clearance certificate from Royal Thai Police Special Branch",
      "Fingerprint form in the prescribed format",
      "Passport and national ID card",
      "House registration or proof of address",
      "Medical certificate on the destination's prescribed form",
      "Panel-clinic health examination results where that system applies",
    ],
    chainTh: [
      "ยื่นคำขอหนังสือรับรองความประพฤติพร้อมพิมพ์ลายนิ้วมือ",
      "รับเอกสารฉบับภาษาอังกฤษจากหน่วยงานผู้ออก",
      "แปลเป็นภาษาปลายทางเมื่อประเทศนั้นไม่รับภาษาอังกฤษ",
      "ยื่นนิติกรณ์ที่กรมการกงสุลเมื่อประเทศปลายทางกำหนด",
      "ยื่นรับรองสถานทูตปลายทางและยื่นภายในช่วงอายุเอกสารที่กำหนด",
    ],
    chainEn: [
      "Apply for the police clearance certificate and provide fingerprints",
      "Collect the English-language certificate from the issuing unit",
      "Translate into the destination language where English is not accepted",
      "Legalise at the Department of Consular Affairs where required",
      "Legalise at the destination embassy and file within the validity window",
    ],
    checklistTh: [
      "ชื่อในหนังสือรับรองความประพฤติตรงกับหนังสือเดินทาง",
      "วันที่ออกเอกสารยังอยู่ในช่วงที่ประเทศปลายทางรับได้",
      "ตรวจว่าปลายทางรับภาษาอังกฤษหรือต้องแปลเป็นภาษาอื่น",
      "ตรวจว่าต้องใช้ panel clinic หรือใบรับรองแพทย์ทั่วไป",
      "เก็บใบเสร็จและหลักฐานการยื่นไว้สำหรับการติดตามผล",
    ],
    checklistEn: [
      "The name on the certificate matches the passport",
      "The issue date still falls inside the destination's validity window",
      "Confirm whether English is accepted or another language is required",
      "Confirm whether a panel clinic or a general medical certificate applies",
      "Keep receipts and filing records for follow-up",
    ],
    faqsTh: [
      {
        q: "หนังสือรับรองความประพฤติขอที่ไหน",
        a: "สำหรับผู้ที่อยู่ในประเทศไทย ขอที่กองบัญชาการตำรวจสันติบาล สำนักงานตำรวจแห่งชาติ ตามขั้นตอนที่หน่วยงานประกาศ ส่วนผู้ที่อยู่ต่างประเทศดำเนินการผ่านสถานเอกอัครราชทูตหรือสถานกงสุลใหญ่ไทยในประเทศที่พำนัก",
      },
      {
        q: "หนังสือรับรองความประพฤติมีอายุเท่าไร",
        a: "ตัวเอกสารไม่ได้ระบุวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางกำหนดช่วงเวลาที่ยอมรับเอง เช่น ต้องออกไม่เกินระยะเวลาหนึ่งก่อนวันยื่น จึงต้องตรวจเงื่อนไขของประเทศปลายทาง",
      },
      {
        q: "ใบรับรองแพทย์ต้องแปลและรับรองไหม",
        a: "ขึ้นอยู่กับระบบของประเทศปลายทาง หากใช้ระบบสถานพยาบาลที่กำหนด ผลตรวจมักส่งตรงถึงหน่วยงานและไม่ต้องแปล แต่หากเป็นใบรับรองแพทย์ทั่วไปที่ออกเป็นภาษาไทย ต้องแปลและอาจต้องผ่านนิติกรณ์ตามที่ปลายทางกำหนด",
      },
      {
        q: "เคยมีคดีความจะขอหนังสือรับรองความประพฤติได้หรือไม่",
        a: "การพิจารณาและเนื้อหาที่ปรากฏในเอกสารเป็นอำนาจของหน่วยงานผู้ออกตามข้อมูลที่มีอยู่ในระบบ เราไม่สามารถระบุผลล่วงหน้าได้ ผู้ยื่นควรสอบถามหน่วยงานผู้ออกโดยตรงเกี่ยวกับกรณีเฉพาะของตน",
      },
    ],
    faqsEn: [
      {
        q: "Where is a Thai police clearance certificate issued?",
        a: "Inside Thailand, at the Royal Thai Police Special Branch under its published procedure. Applicants abroad normally apply through the Royal Thai Embassy or Consulate-General where they live.",
      },
      {
        q: "How long is it valid?",
        a: "The certificate itself carries no expiry, but receiving authorities set their own acceptance window — typically issued within a given period before filing. Check the destination's rule.",
      },
      {
        q: "Do medical certificates need translation and legalisation?",
        a: "Medical certificates require translation and legalisation depending on how they are issued and used. Panel-clinic medical results go directly to the destination authority and need neither translation nor legalisation. Conversely, a general Thai-language medical certificate must be translated and potentially legalised based on the specific rules of the receiving authority.",
      },
      {
        q: "Can someone with a past case obtain a certificate?",
        a: "What appears on the certificate is decided by the issuing authority based on its records; we cannot predict the outcome. Applicants should ask the issuing authority about their specific circumstances.",
      },
    ],
  },
  {
    slug: "family-and-relationship-evidence",
    titleTh: "หลักฐานความสัมพันธ์สำหรับวีซ่าติดตามครอบครัว",
    titleEn: "Relationship evidence for family and dependant visas",
    summaryTh:
      "ทะเบียนสมรส สูติบัตรบุตร หนังสือยินยอมให้บุตรเดินทาง และหลักฐานการอยู่กินร่วมกัน — ชุดเอกสารสำหรับวีซ่าคู่สมรส วีซ่าติดตาม และวีซ่าเยี่ยมญาติ",
    summaryEn:
      "Marriage records, children's birth certificates, parental consent letters and cohabitation evidence — the set used for spouse, dependant and family-visit visas.",
    bodyTh: [
      "วีซ่ากลุ่มติดตามครอบครัวพิจารณาสองเรื่องคู่กัน คือความสัมพันธ์ตามกฎหมายและความสัมพันธ์ที่เป็นจริงในทางปฏิบัติ ความสัมพันธ์ตามกฎหมายพิสูจน์ด้วยเอกสารทะเบียน เช่น ทะเบียนสมรสและสูติบัตร ส่วนความสัมพันธ์ตามข้อเท็จจริงพิสูจน์ด้วยหลักฐานประกอบ เช่น ประวัติการติดต่อ ภาพถ่ายร่วมกัน หลักฐานการโอนเงิน และหลักฐานที่อยู่ร่วมกัน",
      "เอกสารทะเบียนของไทยทั้งหมดต้องแปลเป็นภาษาปลายทางและผ่านนิติกรณ์ที่กรมการกงสุลก่อนยื่นสถานทูต ส่วนเอกสารทะเบียนที่ออกจากต่างประเทศและจะใช้ในไทย ต้องผ่านการรับรองจากประเทศต้นทางก่อน แล้วจึงแปลเป็นภาษาไทยและรับรองนิติกรณ์เพื่อใช้กับสำนักงานตรวจคนเข้าเมืองหรือสำนักทะเบียน",
      "กรณีเด็กเดินทางกับผู้ปกครองเพียงคนเดียวหรือเดินทางลำพัง หลายประเทศกำหนดให้มีหนังสือยินยอมจากบิดามารดาอีกฝ่าย พร้อมสำเนาเอกสารแสดงตนของผู้ให้ความยินยอม หนังสือยินยอมที่ทำในประเทศไทยมักต้องผ่านการรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร ก่อนเข้าสู่ขั้นตอนกรมการกงสุล",
      "กรณีอำนาจปกครองบุตรมีคำสั่งศาลหรือบันทึกข้อตกลงท้ายทะเบียนหย่า ต้องแนบเอกสารดังกล่าวพร้อมคำแปล เพราะเจ้าหน้าที่ปลายทางต้องเห็นว่าใครเป็นผู้มีอำนาจปกครองตามกฎหมาย ก่อนอนุญาตให้เด็กเดินทางหรือพำนักในต่างประเทศ",
      APOSTILLE_TH,
      CONFIRM_TH,
    ],
    bodyEn: [
      "Family and dependant visas assess two things together: the legal relationship and the genuine, ongoing relationship. Registration documents such as marriage certificates and birth certificates prove the first; communication history, photographs, transfer records and shared-address evidence support the second.",
      "Thai registration documents must be translated into the destination language and legalised at the Department of Consular Affairs before the embassy stage. Foreign registration documents intended for use in Thailand must be certified in the issuing country first, then translated into Thai and legalised for the Immigration Bureau or a district registrar.",
      "Where a child travels with one parent or alone, many countries require a consent letter from the other parent with copies of that parent's identity documents. Consent letters executed in Thailand usually need signature certification by a Notarial Services Attorney before the consular stage.",
      "Where custody is governed by a court order or by the agreement recorded on a divorce registration, attach those documents with translations, because the receiving officer must see who holds legal custody before allowing the child to travel or reside abroad.",
      APOSTILLE_EN,
      CONFIRM_EN,
    ],
    documentsTh: [
      "ทะเบียนสมรสและใบสำคัญการสมรส",
      "สูติบัตรของบุตร",
      "ทะเบียนบ้านที่แสดงการอยู่ร่วมกัน",
      "หนังสือยินยอมให้บุตรเดินทางไปต่างประเทศ",
      "คำสั่งศาลเรื่องอำนาจปกครองบุตร หรือบันทึกข้อตกลงท้ายทะเบียนหย่า",
      "หลักฐานความสัมพันธ์ประกอบ เช่น ภาพถ่าย ประวัติการติดต่อ หลักฐานการโอนเงิน",
    ],
    documentsEn: [
      "Marriage registration and marriage certificate",
      "Children's birth certificates",
      "House registration showing a shared address",
      "Parental consent letter for a child travelling abroad",
      "Custody court order or the agreement recorded on the divorce registration",
      "Supporting relationship evidence such as photographs, message history and transfer records",
    ],
    chainTh: [
      "คัดเอกสารทะเบียนฉบับใหม่จากสำนักทะเบียน",
      "จัดทำหนังสือยินยอม/หนังสือรับรองความสัมพันธ์ และรับรองลายมือชื่อผู้ลงนาม",
      "แปลเป็นภาษาปลายทางพร้อมคำรับรองความถูกต้อง",
      "ยื่นนิติกรณ์ที่กรมการกงสุล",
      "ยื่นรับรองสถานทูตปลายทางและยื่นพร้อมคำร้อง",
    ],
    chainEn: [
      "Obtain fresh certified extracts from the registration office",
      "Draft the consent or relationship letter and certify the signatory's signature",
      "Translate into the destination language with a certificate of accuracy",
      "Legalise at the Department of Consular Affairs",
      "Legalise at the destination embassy and submit with the application",
    ],
    checklistTh: [
      "ชื่อคู่สมรสและบุตรสะกดตรงกับหนังสือเดินทางทุกฉบับ",
      "หนังสือยินยอมระบุช่วงเวลาเดินทางและประเทศปลายทางชัดเจน",
      "แนบสำเนาเอกสารแสดงตนของผู้ให้ความยินยอม",
      "หลักฐานความสัมพันธ์เรียงตามลำดับเวลาและอ่านเข้าใจง่าย",
      "เอกสารทะเบียนเป็นฉบับคัดรับรองที่ออกใหม่",
    ],
    checklistEn: [
      "Spouse and children's names match their passports exactly",
      "The consent letter states the travel period and destination clearly",
      "Copies of the consenting parent's identity documents are attached",
      "Relationship evidence is arranged chronologically and easy to follow",
      "Registration documents are newly issued certified extracts",
    ],
    faqsTh: [
      {
        q: "จดทะเบียนสมรสที่ต่างประเทศ ใช้ยื่นในไทยได้เลยไหม",
        a: "ต้องผ่านการรับรองตามระบบของประเทศที่จดทะเบียนก่อน แล้วรับรองที่สถานทูตของประเทศนั้นในไทยหรือสถานเอกอัครราชทูตไทยในประเทศนั้น จากนั้นแปลเป็นภาษาไทยและยื่นนิติกรณ์ที่กรมการกงสุล จึงจะใช้กับหน่วยงานไทยได้",
      },
      {
        q: "หนังสือยินยอมให้บุตรเดินทางต้องทำอย่างไร",
        a: "จัดทำเป็นหนังสือระบุชื่อบุตร ผู้ปกครองที่ให้ความยินยอม ประเทศปลายทางและช่วงเวลาเดินทาง แล้วลงนามต่อหน้าผู้รับรองลายมือชื่อ โดยทั่วไปคือทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร ก่อนนำไปแปลและรับรองตามลำดับที่ปลายทางกำหนด",
      },
      {
        q: "หลักฐานความสัมพันธ์ต้องแปลทั้งหมดหรือไม่",
        a: "โดยทั่วไปเอกสารราชการต้องแปลทั้งหมด ส่วนหลักฐานประกอบ เช่น ภาพถ่ายหรือบทสนทนา บางสถานทูตยอมรับพร้อมคำอธิบายสั้น ๆ ควรตรวจแนวทางของสถานทูตที่รับคำร้อง",
      },
      {
        q: "อยู่กินกันโดยไม่จดทะเบียนสมรส ใช้ยื่นได้ไหม",
        a: "ขึ้นอยู่กับประเภทวีซ่าและกฎของประเทศปลายทาง บางประเทศมีหมวดคู่ชีวิตที่ไม่ได้จดทะเบียนและกำหนดหลักฐานการอยู่ร่วมกันไว้เฉพาะ จึงต้องตรวจเงื่อนไขของประเทศนั้นโดยตรง",
      },
    ],
    faqsEn: [
      {
        q: "We married abroad — can the certificate be used in Thailand directly?",
        a: "A foreign marriage certificate cannot be used in Thailand directly without proper verification. The certificate must first be certified under the issuing country's legal system, then verified by that country's embassy in Thailand or the Royal Thai Embassy abroad, translated into Thai, and legalised by the Department of Consular Affairs before Thai agencies accept it.",
      },
      {
        q: "How is a child travel-consent letter prepared?",
        a: "Draft it naming the child, the consenting parent, the destination and the travel period, then sign before a certifying officer — typically a Notarial Services Attorney — before translation and legalisation as the destination requires.",
      },
      {
        q: "Does all relationship evidence need translating?",
        a: "Official records generally do. For supporting material such as photographs or message logs, some embassies accept the originals with a short explanation. Check the receiving embassy's guidance.",
      },
      {
        q: "We live together but are not married — is that acceptable?",
        a: "Living together without being married is acceptable depending on the specific visa category and destination rules. Some countries offer an unmarried-partner visa route with distinct cohabitation evidence requirements. Applicants should verify the specific criteria directly with the destination country to ensure their relationship status meets the legal standard.",
      },
    ],
  },
];

export function getVisaDocGroup(slug: string): VisaDocGroup | undefined {
  return VISA_DOC_GROUPS.find((g) => g.slug === slug);
}
